import axios from 'axios';
import { RefObject, useEffect } from 'react';

import { REACT_APP_MONOLITH_BASE_URL } from 'runtimeEnvVars';

import { getWxJwt } from './wxJwtToken';

// Not `apiPost`: the shared axios instance signs the recruiter out on any 401, and
// this endpoint's ancestor returns 401 whenever the WX JWT has aged out.
const salesNotificationClient = axios.create({ withCredentials: true });

// Mirrors wx-ui's utils/employer/iframeAuthHandoff.ts.
export const WX_IFRAME_AUTH_REQUEST_SOURCE = 'wx-iframe-auth:request';
export const WX_IFRAME_AUTH_RESPONSE_SOURCE = 'wx-iframe-auth:response';

// Mirrors wx-system's
// frontends/wx-ui/src/utils/employer/poolFreemiumSalesRequest.ts.
export const WX_POOL_FREEMIUM_SALES_REQUEST_SOURCE =
  'wx-pool-freemium-sales:request-v1';

// Absolute: recruiter-ui is served from its own origin, and a bare path would
// hit its static server instead of the monolith.
const POOL_FREEMIUM_SALES_NOTIFICATIONS_URL = `${REACT_APP_MONOLITH_BASE_URL}/internal_api/wx/pool_freemium_sales_notifications`;

/**
 * Delivers only messages whose origin is the iframe's own and whose source is its
 * contentWindow; every channel Hire answers for an embedded WX page shares this boundary.
 */
function useTrustedIframeMessages(
  iframeRef: RefObject<HTMLIFrameElement>,
  url: string | null | undefined,
  onTrustedMessage: (event: MessageEvent, targetOrigin: string) => void,
): void {
  useEffect(() => {
    if (!url) return undefined;

    let targetOrigin: string;
    try {
      targetOrigin = new URL(url).origin;
    } catch (e) {
      return undefined;
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== targetOrigin) return;
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      onTrustedMessage(event, targetOrigin);
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iframeRef, url]);
}

/**
 * Answers `wx-iframe-auth:request` with the cached WX JWT. `targetOrigin`, derived
 * from `url`, is what keeps the token from reaching any other embedder.
 */
export function useWxIframeAuthResponder(
  iframeRef: RefObject<HTMLIFrameElement>,
  url: string | null | undefined,
): void {
  useTrustedIframeMessages(iframeRef, url, (event, targetOrigin) => {
    if (
      (event.data as { source?: string } | undefined)?.source !==
      WX_IFRAME_AUTH_REQUEST_SOURCE
    ) {
      return;
    }

    const token = getWxJwt();
    if (!token) return;

    (event.source as WindowProxy).postMessage(
      { source: WX_IFRAME_AUTH_RESPONSE_SOURCE, token },
      targetOrigin,
    );
  });
}

/**
 * Answers `wx-pool-freemium-sales:request-v1` by calling Hire's sales-notification
 * endpoint. The upsell confirms optimistically, so the outcome is deliberately not surfaced.
 */
export function useWxPoolFreemiumSalesRequestResponder(
  iframeRef: RefObject<HTMLIFrameElement>,
  url: string | null | undefined,
): void {
  useTrustedIframeMessages(iframeRef, url, event => {
    if (
      (event.data as { source?: string } | undefined)?.source !==
      WX_POOL_FREEMIUM_SALES_REQUEST_SOURCE
    ) {
      return;
    }

    salesNotificationClient
      .post(POOL_FREEMIUM_SALES_NOTIFICATIONS_URL, {})
      .catch(() => {
        // Intentionally silent: see the doc comment above.
      });
  });
}
